import { createSelector, createSlice } from "@reduxjs/toolkit";

import { setDeep } from "Libs/objectAccess";
import logger from "Libs/logger";
import { createAppAsyncThunk } from "Store/createAppAsyncThunk";
import { getNewBillingSystemError } from "Hooks/billingSystem/getNewBillingSystemError";

import { getProjectEstimates } from "Api/billingSystem/costs/getProjectEstimate.request";

import type { EstimationProject } from "Api/billingSystem/types";
import type { RootState } from "Store/configureStore";

const getEstimateInfo = (
  currentMonth: boolean
): { param: string; key: "current" | "future" } => ({
  param: currentMonth ? `?current_month=${true}` : "",
  key: currentMonth ? "current" : "future"
});

// Thunk
export const getProjectEstimatesThunk = createAppAsyncThunk(
  "billingSystem/projectEstimate/get",
  async (
    {
      billingProfileId,
      projectId,
      currentMonth
    }: {
      billingProfileId: string;
      projectId: string;
      currentMonth: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      const { param } = getEstimateInfo(currentMonth);
      const projectEstimates = await getProjectEstimates({
        billingProfileId,
        projectId,
        param
      });
      return { projectId, currentMonth, projectEstimates };
    } catch (err) {
      logger(err, {
        action: "get_project_estimates",
        billingProfileId,
        projectId
      });
      return rejectWithValue({
        error: getNewBillingSystemError(err) ?? "Something went wrong"
      });
    }
  }
);

type EstimateData = {
  projectEstimates?: EstimationProject;
  loading: boolean;
  error?: string;
};
// State type
type ProjectEstimateState = {
  dataByProjectId: {
    [projectId: string]: {
      current?: EstimateData;
      future?: EstimateData;
    };
  };
};

const initialState: ProjectEstimateState = {
  dataByProjectId: {}
};

// Slice
const projectEstimateSlice = createSlice({
  name: "projectEstimate",
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getProjectEstimatesThunk.pending, (state, { meta }) => {
        const { projectId, currentMonth } = meta.arg;
        const { key } = getEstimateInfo(currentMonth);

        setDeep(state, ["dataByProjectId", projectId, key, "loading"], true);
        setDeep(state, ["dataByProjectId", projectId, key, "error"], undefined);
      })
      .addCase(getProjectEstimatesThunk.fulfilled, (state, { payload }) => {
        const { projectId, currentMonth, projectEstimates } = payload;
        const { key } = getEstimateInfo(currentMonth);
        setDeep(state, ["dataByProjectId", projectId, key, "loading"], false);
        setDeep(
          state,
          ["dataByProjectId", projectId, key, "projectEstimates"],
          projectEstimates
        );
      })
      .addCase(
        getProjectEstimatesThunk.rejected,
        (state, { meta, payload }) => {
          const { projectId, currentMonth } = meta.arg;
          const { key } = getEstimateInfo(currentMonth);
          setDeep(state, ["dataByProjectId", projectId, key, "loading"], false);
          setDeep(
            state,
            ["dataByProjectId", projectId, key, "error"],
            payload?.error
          );
        }
      );
  }
});

export default projectEstimateSlice.reducer;

// Selectors
const projectEstimateStateSelector = (state: RootState) =>
  state.projectEstimate;

export const selectProjectEstimates = createSelector(
  projectEstimateStateSelector,
  (_: RootState, projectId: string) => projectId,
  (state, projectId) => state.dataByProjectId[projectId]
);

export const selectCurrentMonthEstimates = createSelector(
  selectProjectEstimates,
  data => data?.current?.projectEstimates
);

export const selectCurrentMonthLoading = createSelector(
  selectProjectEstimates,
  data => data?.current?.loading ?? false
);

export const selectCurrentMonthError = createSelector(
  selectProjectEstimates,
  data => data?.current?.error
);

export const selectFutureMonthEstimates = createSelector(
  selectProjectEstimates,
  data => data?.future?.projectEstimates
);

export const selectFutureMonthLoading = createSelector(
  selectProjectEstimates,
  data => data?.future?.loading ?? false
);

export const selectFutureMonthError = createSelector(
  selectProjectEstimates,
  data => data?.future?.error
);
